import { useId, useState } from 'react';
import { CloseOutlined, InboxOutlined } from '@ant-design/icons';
import { Upload } from 'antd';

import { Button, Dialog } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { dungLuongVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { CanhBaoTrongHop, LoiTrongHop } from '../../tenant/components/phan-form';
import { KHOA_LOI_TEP } from '../constants';
import { useTaiNhieuTep } from '../hooks/useTriThuc';
import { DUOI_NHAN, TOI_DA_MB, kiemHieuLuc, kiemTep, ngayHomNay } from '../nghiep-vu/tri-thuc';
import type { LoiTep } from '../nghiep-vu/tri-thuc';
import { ChonHieuLuc } from './ChonHieuLuc';

interface TepCho {
  khoa: string;
  tep: File;
  /** Lỗi của lần gửi trước, nguyên văn đã dịch. */
  loi?: string;
}

/**
 * Tải tệp vào bộ tri thức đang mở (`POST /files`), một hay nhiều tệp cùng một khoảng hiệu lực. Tệp đi
 * thẳng vào hàng đợi index của module, không qua kiểm duyệt — tài liệu cần duyệt tải ở màn "Upload tài
 * liệu".
 */
export function FormTaiTep({
  kbId,
  thuMuc,
  tenThuMuc,
  open,
  onClose,
}: {
  kbId: string;
  thuMuc: string | null;
  tenThuMuc: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const tai = useTaiNhieuTep();

  const [ds, setDs] = useState<TepCho[]>([]);
  const [tuChoi, setTuChoi] = useState<Array<{ ten: string; ma: LoiTep }>>([]);
  const [daLenChuaVaoThuMuc, setDaLenChuaVaoThuMuc] = useState<string[]>([]);
  const [tu, setTu] = useState<string | null>(ngayHomNay());
  const [den, setDen] = useState<string | null>(null);
  const [daGui, setDaGui] = useState(false);
  const [loiChung, setLoiChung] = useState<{ loi: unknown; truong: Record<string, string> } | null>(null);

  const maLoiHieuLuc = kiemHieuLuc(tu, den, true);
  const loiHieuLuc = maLoiHieuLuc && (daGui || maLoiHieuLuc === 'denKhongSauTu') ? maLoiHieuLuc : null;
  const loiChuaChon = daGui && ds.length === 0;

  const them = (tep: File) => {
    const ma = kiemTep(tep);
    if (ma) {
      setTuChoi((cu) => [...cu, { ten: tep.name, ma }]);
      return;
    }
    setDs((cu) => [...cu, { khoa: `${tep.name}-${tep.size}-${tep.lastModified}-${cu.length}`, tep }]);
  };

  const gui = async () => {
    setDaGui(true);
    if (ds.length === 0 || maLoiHieuLuc || tai.isPending) return;
    setLoiChung(null);
    try {
      const kq = await tai.mutateAsync({
        du: { knowledge_base_id: kbId, effective_from: tu!, ...(den ? { effective_to: den } : {}) },
        tep: ds.map((x) => x.tep),
        thuMuc,
      });
      // Còn lỗi: giữ lại đúng các tệp hỏng kèm lý do, bỏ những tệp đã lên.
      const conLai = kq.loi.filter((l) => !l.daTaiLen);
      setDaGui(false);
      setDaLenChuaVaoThuMuc(kq.loi.filter((l) => l.daTaiLen).map((l) => l.tep.name));
      if (kq.loi.length === 0) {
        onClose();
        return;
      }
      setDs(conLai.map((l, i) => ({ khoa: `${l.tep.name}-${i}`, tep: l.tep, loi: dichLoi(l.loi) })));
      // Lỗi theo trường (hiệu lực sai) giống nhau ở mọi tệp: đưa lên ô ngày.
      const truong = conLai.map((l) => loiTheoTruong(l.loi)).find((x) => Object.keys(x).length > 0);
      if (truong) setLoiChung({ loi: conLai[0]!.loi, truong });
    } catch (err) {
      setLoiChung({ loi: err, truong: loiTheoTruong(err) });
    }
  };

  const truong = loiChung?.truong ?? {};
  const loiDai = loiChung && Object.keys(truong).length === 0 ? loiChung.loi : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('triThuc.tep.taiLenTieuDe')}
      width={520}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loiDai) && <LoiTrongHop>{dichLoi(loiDai)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={tai.isPending}>
              {tai.isPending
                ? t('triThuc.tep.dangTaiLen')
                : ds.length > 1
                  ? t('triThuc.tep.taiLenSo', { so: soVN(ds.length, locale) })
                  : t('triThuc.tep.taiLen')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <p className="text-xs text-content-muted">
          {t('triThuc.tep.taiVao', { noi: tenThuMuc ?? t('triThuc.thuMuc.gocBo') })}
        </p>

        <div className="space-y-2">
          <Upload.Dragger
            multiple
            accept={DUOI_NHAN.join(',')}
            showUploadList={false}
            beforeUpload={(tep) => {
              them(tep);
              setLoiChung(null);
              return Upload.LIST_IGNORE;
            }}
          >
            <p className="text-2xl text-primary-600">
              <InboxOutlined aria-hidden />
            </p>
            <p className="text-sm font-medium text-content-primary">{t('triThuc.tep.keoTha')}</p>
            <p className="text-xs text-content-muted">
              {t('triThuc.tep.keoThaMo', { so: soVN(TOI_DA_MB, locale) })}
            </p>
          </Upload.Dragger>
          {loiChuaChon && (
            <p role="alert" className="text-xs text-danger-600">
              {t('triThuc.tep.loiChuaChon')}
            </p>
          )}
          {tuChoi.length > 0 && (
            <LoiTrongHop>
              <span className="block">{t('triThuc.tep.khongNhan')}</span>
              {tuChoi.map((x, i) => (
                <span key={i} className="block break-all">
                  {t('triThuc.tep.tepVaLyDo', { ten: x.ten, lyDo: t(KHOA_LOI_TEP[x.ma], { so: soVN(TOI_DA_MB, locale) }) })}
                </span>
              ))}
            </LoiTrongHop>
          )}
          {daLenChuaVaoThuMuc.length > 0 && (
            <CanhBaoTrongHop>
              {t('triThuc.tep.chuaVaoThuMuc', { ten: daLenChuaVaoThuMuc.join(', ') })}
            </CanhBaoTrongHop>
          )}
          {ds.length > 0 && (
            <ul className="max-h-40 space-y-1.5 overflow-y-auto" aria-label={t('triThuc.tep.daChon', { so: soVN(ds.length, locale) })}>
              {ds.map((x) => (
                <li key={x.khoa} className="rounded border border-default bg-subtle px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-content-primary" title={x.tep.name}>
                      {x.tep.name}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-content-muted">{dungLuongVN(x.tep.size, locale)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={t('triThuc.tep.boTep', { ten: x.tep.name })}
                      aria-label={t('triThuc.tep.boTep', { ten: x.tep.name })}
                      icon={<CloseOutlined />}
                      onClick={() => setDs((cu) => cu.filter((y) => y.khoa !== x.khoa))}
                    />
                  </div>
                  {x.loi && <p className="mt-0.5 text-xs text-danger-700">{x.loi}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <ChonHieuLuc
          tu={tu}
          den={den}
          batBuocTu
          loi={loiHieuLuc}
          loiServer={{ tu: truong['/effective_from'], den: truong['/effective_to'] }}
          onDoi={(a, b) => {
            setTu(a);
            setDen(b);
            setLoiChung(null);
          }}
        />
      </form>
    </Dialog>
  );
}
